import React, { Component } from 'react';

import formatDate from '../core/support/formatDate';

import {
  Button,
  Divider,
  Heading,
  HStack,
  LoadingIndicator,
  ScrollPane,
  Select,
  Spacer,
  TableView,
  Text,
  VStack,
} from '../core-ui';
import VendorPortalPageEditor from './VendorPortalAdmin/VendorPortalPageEditor';
import { canUser } from '../support/shared/permissionHelpers';

import { useQuery } from '@apollo/client';
import {
  GetPortalPages,
  GetPortalPages_myAccount_currentSeason_vendorPortalPages as VendorPortalPage,
} from './VendorPortalAdmin/__generated__/GetPortalPages';
import { useAuthState } from './AuthState';
import { GET_PORTAL_PAGES } from './VendorPortalAdmin/queries';

const pageFilterMap = {
  All: {
    label: t('All'),
    labelWithStatus: t('Status: All'),
  },
  Draft: {
    label: t('Draft'),
    labelWithStatus: t('Status: Draft'),
  },
  Published: {
    label: t('Published'),
    labelWithStatus: t('Status: Published'),
  },
};

type PageFilter = keyof typeof pageFilterMap;

type Props = {
  pages: Array<VendorPortalPage>;
  canEdit: boolean;
};

type State = {
  statusFilter: PageFilter;
  showEditor: { action: 'CREATE' } | { action: 'EDIT'; pageId: string } | null;
  sortBy: [number, boolean];
};

class VendorPortalPagesView extends Component<Props, State> {
  state: State = {
    statusFilter: 'All',
    showEditor: null,
    sortBy: [0, false],
  };

  render() {
    const { canEdit, pages } = this.props;
    const { showEditor } = this.state;
    const editingPage =
      showEditor?.action === 'EDIT'
        ? pages.find((page) => page.id === showEditor.pageId)
        : undefined;
    const shouldShowTable = showEditor == null;
    return (
      <>
        <VStack
          px={10}
          pt={10}
          flex={1}
          minH={0}
          alignItems="stretch"
          spacing={2}
          display={shouldShowTable ? undefined : 'none'}
        >
          <HStack>
            <Heading as="h2" size="lg">
              {t('Pages')}
            </Heading>
            <Spacer />
            <Select
              width="auto"
              options={Object.keys(pageFilterMap)}
              value={this.state.statusFilter}
              onChange={(status) => {
                this.setState({ statusFilter: status });
              }}
              getLabel={(item) => pageFilterMap[item].labelWithStatus}
            />
            {canEdit && (
              <Button
                variant="solid"
                colorScheme="blue"
                onClick={() => {
                  this.setState({ showEditor: { action: 'CREATE' } });
                }}
              >
                {t('Add New')}
              </Button>
            )}
          </HStack>
          <Divider borderColor="gray.200" />
          <ScrollPane>{this._renderPageTable()}</ScrollPane>
        </VStack>
        {showEditor?.action === 'CREATE' ? (
          <ScrollPane backgroundColor="#eff3f8">
            <VendorPortalPageEditor
              key="CREATE"
              onClose={() => {
                this.setState({ showEditor: null });
              }}
              canEdit={canEdit}
            />
          </ScrollPane>
        ) : null}
        {editingPage ? (
          <ScrollPane backgroundColor="#eff3f8">
            <VendorPortalPageEditor
              key={editingPage.id}
              vendorPortalPage={editingPage}
              onClose={() => {
                this.setState({ showEditor: null });
              }}
              canEdit={canEdit}
            />
          </ScrollPane>
        ) : null}
      </>
    );
  }

  _renderPageTable = () => {
    const { pages } = this.props;
    const { sortBy, statusFilter } = this.state;

    return (
      <TableView
        data={pages.filter((page) => {
          return statusFilter === 'All' ? true : statusFilter === page.status;
        })}
        onRowClick={(page) => {
          this.setState({ showEditor: { action: 'EDIT', pageId: page.id } });
        }}
        onSortChange={(sortBy) => this.setState({ sortBy })}
        sortBy={sortBy}
        columns={[
          {
            title: t('Title'),
            render: (page) => page.title,
            sortable: {
              sortFn: (a, b) => a.title.localeCompare(b.title),
            },
          },
          {
            title: t('Status'),
            render: (page) => pageFilterMap[page.status].label,
            sortable: {
              sortFn: (a, b) => a.status.localeCompare(b.status),
            },
          },
          {
            title: t('Visibility'),
            render: (page) => (page.isPublic ? t('Public') : t('Private')),
            sortable: {
              sortFn: (a, b) => Number(a.isPublic) - Number(b.isPublic),
            },
          },
          {
            title: t('Last modified'),
            render: (page) => formatDate(page.updatedAt),
            sortable: {
              sortFn: (a, b) => {
                return Date.parse(a.updatedAt) - Date.parse(b.updatedAt);
              },
            },
          },
        ]}
      />
    );
  };
}

export default function VendorPortalPages() {
  const { user } = useAuthState();
  const { data, loading, error } = useQuery<GetPortalPages>(GET_PORTAL_PAGES);

  if (error) {
    return (
      <VStack flex={1} justify="center" align="center">
        <Text>{String(error)}</Text>
      </VStack>
    );
  }

  if (loading || !data) {
    return (
      <VStack flex={1} justify="center" align="center">
        <LoadingIndicator />
      </VStack>
    );
  }

  const { currentSeason } = data.myAccount;
  const canEdit = canUser(user, { marketPages: 'WRITE' });
  return (
    <VendorPortalPagesView
      pages={currentSeason.vendorPortalPages}
      canEdit={canEdit}
    />
  );
}
